import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Notification, IToDo } from 'app/layout/common/notifications/notifications.types';
import { interval, map, Observable, ReplaySubject, Subscription, switchMap, take, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class NotificationsService {
    private _notifications: ReplaySubject<Notification[]> = new ReplaySubject<Notification[]>(1);

    private _pollingSubscription: Subscription | null = null;


    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient) { }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for notifications
     */
    get notifications$(): Observable<Notification[]> {
        return this._notifications.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------


    /**
     * API call for notifications
     */
    private _getNotifications(): Observable<Notification[]> {
        return this._httpClient.get<any>(`${environment.baseUrl}/todo/Pending/list`)
            .pipe(
                map((response) => {
                    const todos = response?.result ?? [];

                    return todos.map((todo: IToDo) => ({
                        id: todo.id,
                        title: todo.camp?.name || 'Common',
                        description: todo.notes,
                        time: todo.createdDate,
                        read: todo.isCompleted,
                        icon: 'heroicons_outline:bell-alert',
                        todo: todo
                    }));
                })
            );
    }



    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------


    /**
     * Start notification polling (every 5 seconds)
     */
    startNotificationPolling(): void {
        if (this._pollingSubscription) {
            return;
        }

        this._pollingSubscription = interval(30000)
            .pipe(
                switchMap(() => this._getNotifications())
            )
            .subscribe({
                next: (notifications) => {
                    this._notifications.next(notifications);
                },
                error: (error) => {
                    console.error('Polling error:', error);
                }
            });
    }

    /**
     * Stop polling
     */
    stopNotificationPolling(): void {
        if (this._pollingSubscription) {
            this._pollingSubscription.unsubscribe();
            this._pollingSubscription = null;
        }
    }


    /**
     * Get all notifications
     */
    getAll(): Observable<Notification[]> {
        return this._getNotifications().pipe(
            tap((notifications) => {
                this._notifications.next([...notifications]); // force new reference
            })
        );
    }

    /**
     * Delete the notification
     *
     * @param id
     */
    delete(id: string): Observable<boolean> {
        return this._httpClient.
            delete<boolean>(`${environment.baseUrl}/todo/${id}`)
            .pipe(
                tap(() => {

                    this.notifications$.pipe(take(1)).subscribe((notifications) => {
                        // Find the index of the deleted notification
                        const index = notifications.findIndex(
                            (item) => item.id === id
                        );

                        if (index > -1) {
                            notifications.splice(index, 1);
                            this._notifications.next([...notifications]);
                        }
                    });
                })
            );
    }
}
